/**
 * TenantGate — resolves the URL's tenant and makes it available *before* login.
 *
 * Wraps each app's shell. What it provides is presentation only: which brand the
 * landing and login screens wear, and whether a register form is offered. It is
 * never an authorization input — reads are scoped by identity in the DAL and
 * tenant membership comes from an invite row.
 *
 * See docs/FeatureDesigns/multi-tenancy/tenant-urls.md §5 and §6.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchTenantContext, type TenantContext } from './api';
import { currentTenantPrefix, hostSuggestsTenant } from './prefix';

export interface TenantState {
    /** The resolved tenant, or `null` on a shared host / unresolved URL. */
    tenant: TenantContext | null;
    /** True while the lookup is in flight. Only ever true when the URL actually
     *  suggested a tenant. */
    loading: boolean;
}

const TenantStateContext = createContext<TenantState>({ tenant: null, loading: false });

/** The tenant this URL addresses, or `null`. Safe to call from any app. */
export function useTenant(): TenantState {
    return useContext(TenantStateContext);
}

/**
 * Whether the current URL means to address a tenant at all.
 *
 * Computed synchronously so the common case — a plain `buzzbrain.ai` load —
 * skips the request entirely rather than paying a round-trip to be told there is
 * no tenant.
 */
function urlSuggestsTenant(): boolean {
    if (currentTenantPrefix().slugFromPath) return true;
    return typeof window !== 'undefined' && hostSuggestsTenant(window.location.hostname);
}

export function TenantGate({ children }: { children: ReactNode }) {
    const { slugFromPath } = currentTenantPrefix();
    const [state, setState] = useState<TenantState>(() => ({
        tenant: null,
        loading: urlSuggestsTenant(),
    }));
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!urlSuggestsTenant()) return;
        let cancelled = false;

        fetchTenantContext(slugFromPath).then(tenant => {
            if (cancelled) return;
            // An explicit `/t/<slug>` that resolves to nothing is a dead end
            // worth naming: somebody was handed that link. A host that merely
            // looks tenant-ish (a stray or mistyped subdomain) falls back to the
            // default brand instead of bricking the app.
            if (!tenant && slugFromPath) setNotFound(true);
            setState({ tenant, loading: false });

            // A retired slug still resolves; move the user onto the current URL
            // so links and bookmarks converge rather than living on forever.
            if (tenant?.stale && tenant.canonicalUrl) {
                window.location.replace(tenant.canonicalUrl + window.location.search);
            }
        });

        return () => { cancelled = true; };
    }, [slugFromPath]);

    if (notFound) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                minHeight: '100vh', padding: 24, textAlign: 'center', gap: 8,
            }}>
                <h1 style={{ margin: 0, fontSize: 20 }}>No such organisation</h1>
                <p style={{ margin: 0, color: 'var(--text-secondary, #666)', maxWidth: 420 }}>
                    We couldn&apos;t find <strong>{slugFromPath}</strong>. Check the link you were
                    given, or ask whoever invited you for a new one.
                </p>
            </div>
        );
    }

    // Deliberately renders children while loading rather than showing a spinner:
    // only pre-login *branding* depends on this, and every app already handles a
    // brand that arrives a beat later. Blocking here would add a round-trip to
    // first paint on tenant URLs.
    return (
        <TenantStateContext.Provider value={state}>
            {children}
        </TenantStateContext.Provider>
    );
}
